/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {type MRT_ColumnDef} from 'material-react-table';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import toast from 'react-hot-toast';
import {useDispatch} from 'react-redux';
import {EUserPermissionAccess} from '../../../../redux/ducks/AccessControl/types';
import {stringToDateToLocaleString} from "../../../../helpers/helpers";
import {usePostRequest} from '../../../../api/helper';
import {selectAccessControlState} from '../../../../redux/ducks/AccessControl/slice';
import {selectDialogState, setIsAddStudyDialogOpen, setIsDeleteStudyDialogOpen, setIsEditStudyDialogOpen} from '../../../../redux/ducks/CoverageRequirements/slice';
import {selectGlobalDialogState, setGlobalDialogComponent} from '../../../../redux/ducks/GlobalDialog/slice';
import {useAppSelector} from '../../../../redux/hooks';
import {AppDispatch} from '../../../../redux/store';
import ArgosDialog, {StyledArgosDialog} from '../../../components/Dialogs/ArgosDialog';
import {IComponent} from '../../../components/Global/Dialog/GlobalDialog';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {addNewStudyEndpoint, COVERAGE_REQUIREMENTS_CONSTANTS, coverageRequirementsStudiesDataEndpoint, editStudyEndPoint, usersDataEndpoint} from '../constants';
import {Container, defaultTextFilterMode, defaultTextFilterModeOptions, getCustomSelectionList, getSelectionList, IFilterSelect, renderNameString, Row, tableRowActions, tableToolbarActions} from './constants';
import AddStudyDialog from './Dialogs/AddStudyDialog';
import {dialogHeight, dialogWidth, User} from './Dialogs/constants';
import EditStudyDialog from './Dialogs/EditStudyDialog';
import {hasThisAccessLevelToRouteKey, useGetCurrentPageUserAccessLevel, useHasWriteAccess} from "../../../../redux/ducks/AccessControl/accessControlHelpers";
import {EArgosRouteKeys} from "../../../../Routes/constants";
import Loader from '../../../components/Loader/Loader';
import BoxWithCenteredContent from '../../../components/Containers/BoxWithCenteredContent';
import ArgosButton, {ArgosLoadingButton} from '../../../components/Buttons/ArgosButton';
import ArgosFlexbox from '../../../components/Containers/ArgosFlexbox';
import {ELoadingStateTypes, selectLoadingState, setLoadingState1} from '../../../../redux/ducks/Misc/slice';
import {ArgosTable} from '../../../components/Table/ArgosTable';
import {Checkbox} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";

export const goToAddStudyDialog = (dispatch: AppDispatch) => {
  dispatch(setIsAddStudyDialogOpen(true))
}

export const goToEditStudyDialog = (dispatch: AppDispatch, component: IComponent) => {
  dispatch(setGlobalDialogComponent(component))
  dispatch(setIsEditStudyDialogOpen(true))
}

export const goToDeleteStudyDialog = (dispatch: AppDispatch, component: IComponent) => {
  dispatch(setGlobalDialogComponent(component))
  dispatch(setIsDeleteStudyDialogOpen(true))
}

const Table = () => {
  const studiesFetchHandle = usePostRequest(coverageRequirementsStudiesDataEndpoint)
  const usersFetchHandle = usePostRequest(usersDataEndpoint)
  const addStudyFetchHandle = usePostRequest(addNewStudyEndpoint)
  const editStudyFetchHandle = usePostRequest(editStudyEndPoint)
  const dispatch = useDispatch()
  const hasWriteAccess = useHasWriteAccess()
  const [isUserAdmin, userAccessLevel] = useGetCurrentPageUserAccessLevel()
  const userId = useAppSelector((state) => selectAccessControlState(state)?.currentUser?.id)
  const isAddStudyDialogOpen = useAppSelector(state => selectDialogState(state).isAddStudyDialogOpen)
  const isEditStudyDialogOpen = useAppSelector(state => selectDialogState(state).isEditStudyDialogOpen)
  const [studiesData, setStudiesData] = useState<Row[]>([])
  const [userIdNameMap, setUserIdNameMap] = useState({})
  const [isTableLoading, setIsTableLoading] = useState(true)
  const studyDetailsDialogOpen = useAppSelector(state => selectGlobalDialogState(state).isOpen)
  const addStudy = (requestData: any) => {
    addStudyFetchHandle(requestData)
      .then((data) => {
        setStudiesData(data)
        fetchData()
      })
      .catch((e) => {
        toast.error("Unable to add study, please try again")
      })
  }

  const editStudy = (editData: any) => {
    editStudyFetchHandle(editData)
      .then((data) => {
        setStudiesData(data)
      })
      .catch((e) => {
        toast.error("Unable to add study, please try again")
      })
  }

  const [filterSelectList, setFilterSelectList] = useState<IFilterSelect>({
    owner: [],
    status: []
  })
  const parseRank = (str: any) => {
    let result = 0
    if (str != "0")
      result = parseInt(str.split('(')[1].split('%')[0])
    return result
  }
  const sortFu = (rowA: any, rowB: any, columnId: any) => {
    return parseRank(rowA.getValue(columnId)) < parseRank(rowB.getValue(columnId)) ? 1 : -1
  }
  const getColor = (str: any) => {
    let result = ''
    if (str != "0") {
      const val = parseRank(str)
      switch (true) {
        case val == 100 :
          result = 'green';
          break;
      }
    }
    return result
  }
  const tableColumns = useMemo<MRT_ColumnDef<Row>[]>(
    () => [
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Name',
        filterFn: defaultTextFilterMode,
        columnFilterModeOptions: defaultTextFilterModeOptions,
        size: 250,
      },
      {
        accessorKey: 'description',
        id: 'description',
        header: 'Description',
        filterFn: defaultTextFilterMode,
        columnFilterModeOptions: defaultTextFilterModeOptions,
        size: 250,
      },
      {
        accessorKey: 'owner',
        id: 'owner',
        header: 'Owner',
        filterVariant: 'multi-select',
        filterSelectOptions: filterSelectList.owner,
        enableColumnFilterModes: false,
        Cell: ({cell}: any) => renderNameString(cell.getValue(), userIdNameMap) ?? '',
      },
      {
        accessorFn: (row: any) => new Date(row.dateCreated),
        id: 'dateCreated',
        header: 'Date Created',
        filterFn: 'betweenInclusive',
        filterVariant: 'date',
        enableColumnFilterModes: false,
        sortingFn: 'datetime',
        Cell: ({cell}: any) => stringToDateToLocaleString(cell.getValue()) ?? '',
      },
      {
        accessorFn: (row: any) => new Date(row.dateModified) ?? '',
        id: 'dateModified',
        header: 'Date Modified',
        filterFn: 'betweenInclusive',
        filterVariant: 'date',
        enableColumnFilterModes: false,
        sortingFn: 'datetime',
        Cell: ({cell}: any) => stringToDateToLocaleString(cell.getValue()),
      },
      {
        accessorKey: 'isPlanOfRecord',
        id: 'isPlanOfRecord',
        header: 'Plan of Record',
        filterVariant: 'checkbox',
        Cell: ({cell}: any) => <FormControlLabel
          control={<Checkbox
            style={{justifyContent: "center"}}
            checked={cell.row.original.isPlanOfRecord}
            disabled={true}
          />} label=""
          style={{justifyContent: "center"}}></FormControlLabel>

      },
      {
        accessorKey: 'isStudyArchive',
        id: 'isStudyArchive',
        header: 'Archive',
        filterVariant: 'checkbox',
        Cell: ({cell}: any) => <FormControlLabel
          control={<Checkbox
            style={{justifyContent: "center"}}
            checked={cell.row.original.isStudyArchive}
            disabled={true}
          />} label=""
          style={{justifyContent: "center"}}></FormControlLabel>

      },
      {
        accessorKey: 'rawCompStatus',
        id: 'rawCompStatus',
        header: 'Raw Data Set Completion Status',
        size: 250,
        sortingFn: (rowA: any, rowB: any, columnId: any): number => sortFu(rowA, rowB, columnId),
        muiTableBodyCellProps: ({
                                  cell
                                }: any) => ({
          sx: {
            backgroundColor: cell.getValue() && getColor(cell.getValue()),
          }
        })

      },
      {
        accessorKey: 'mergCompStatus',
        id: 'mergCompStatus',
        header: 'Merge Data Set Completion Status',
        size: 250,
        sortingFn: (rowA: any, rowB: any, columnId: any): number => sortFu(rowA, rowB, columnId),
        muiTableBodyCellProps: ({
                                  cell
                                }: any) => ({
          sx: {
            backgroundColor: cell.getValue() && getColor(cell.getValue()),
          }
        })

      },
      {
        accessorKey: 'compStatus',
        id: 'compStatus',
        header: 'Total Completion Status',
        size: 200,
        sortingFn: (rowA: any, rowB: any, columnId: any): number => sortFu(rowA, rowB, columnId),
        muiTableBodyCellProps: ({
                                  cell
                                }: any) => ({
          sx: {
            backgroundColor: cell.getValue() && getColor(cell.getValue()),
          }
        })
      },
    ],
    [filterSelectList],
  )
  const setSelectionLists = (data: any, userIdNameMap: any) => {
    let ownerList = getCustomSelectionList(data, "owner", userIdNameMap)
    let statusList = getSelectionList(data, "status")
    setFilterSelectList({owner: ownerList, status: statusList})
  }

  const fetchData = useCallback(async () => {
    try {
      setIsTableLoading(true);
      const usersData = await usersFetchHandle();
      const filteredUsers = usersData
        .filter((element: any) =>
          hasThisAccessLevelToRouteKey(EArgosRouteKeys.COVERAGE_REQUIREMENTS, element, EUserPermissionAccess.READ_WRITE)
        )
        .map((elem: User) => ({
          [elem.id]: `${elem.firstName} ${elem.lastName}`
        }));
      const userIdNames = Object.assign({}, ...filteredUsers);
      setUserIdNameMap(userIdNames);

      const studiesData = await studiesFetchHandle();
      setStudiesData(studiesData);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load data");
    } 
    finally {
      setIsTableLoading(false);
    }
  }, []);

  useEffect(() => {
    setIsTableLoading(true)
    fetchData()
  }, [studyDetailsDialogOpen])

  useEffect(() => {
    setSelectionLists(studiesData, userIdNameMap)

  }, [userIdNameMap])

  const isDeleteStudyLoadingState = useAppSelector((state) => selectLoadingState(state, ELoadingStateTypes.DELETE_COVERAGE_REQUIREMENTS_STUDY))
  const [openDeleteDialog, setOpenDeleteDialog] = useState<any>(null)
  const deleteStudyHandle = usePostRequest(COVERAGE_REQUIREMENTS_CONSTANTS.landing.endpoints.deleteCoverageRequirementsStudy)
  const shouldDeleteStudyHandle = usePostRequest(COVERAGE_REQUIREMENTS_CONSTANTS.landing.endpoints.shouldDeleteCoveragerequirementsStudy)
  const isDeleteStudyLoading = isDeleteStudyLoadingState?.isLoading ?? false
  const initialDeleteDialogLoading = isDeleteStudyLoading && openDeleteDialog?.initialLoad

  const shouldDeleteCoverageRequirementsStudy = (row: any) => {
    const coverageRequirementsStudyId = row?.id
    dispatch(setLoadingState1(ELoadingStateTypes.DELETE_COVERAGE_REQUIREMENTS_STUDY, true, {id: coverageRequirementsStudyId}))
    let _deleteDialogData: any = {
      data: {id: coverageRequirementsStudyId},
    }
    shouldDeleteStudyHandle({coverageRequirementsStudyId: coverageRequirementsStudyId})
      .then((res: any) => {
        dispatch(setLoadingState1(ELoadingStateTypes.DELETE_COVERAGE_REQUIREMENTS_STUDY, false, {id: coverageRequirementsStudyId}))
        if (res?.shouldDelete) {
          _deleteDialogData.message = res?.message
        } else {
          const errorMsg = res?.message ?? "Failed to check if program can be deleted."
          _deleteDialogData.deleteInvalid = true
          _deleteDialogData.message = errorMsg
          const mapAdasProgramToReports = res?.mapAdasProgramToReports ?? null
          // setOpenDeleteDialog({
          //   message: errorMsg,
          //   data: {
          //     studyId: coverageRequirementsStudyId,
          //     mapAdasProgramToReports
          //   }
          // })
          _deleteDialogData.data.mapAdasProgramToReports = mapAdasProgramToReports
        }
        setOpenDeleteDialog(_deleteDialogData)
      })
      .catch(err => {
        _deleteDialogData.deleteInvalid = true
        _deleteDialogData.message = "Failed to check if study can be deleted."
        setOpenDeleteDialog(_deleteDialogData)
        console.log('err', err)
      }).finally(() => {
      dispatch(setLoadingState1(ELoadingStateTypes.DELETE_COVERAGE_REQUIREMENTS_STUDY, false, {id: coverageRequirementsStudyId}))
    })
  }
  // Using openDeleteDialog.mapAdasProgramToReports, create jsx of bulleted list where key is first level, and nested in there loop value list and use item.reportName
  const jsxBulletedList = useMemo(() => {
    const mapAdasProgramToReports = openDeleteDialog?.data?.mapAdasProgramToReports ?? null
    if (!mapAdasProgramToReports) {
      return null
    }
    const jsx = []
    for (const [key, value] of Object.entries<any>(mapAdasProgramToReports)) {
      jsx.push(<li key={key}>{key}</li>)
      const nestedKey = `${key}-ul`
      jsx.push(<ul key={nestedKey}>{value.map((item: any) => <li key={`${nestedKey}-${item.reportName}`}>{item.reportName}</li>)}</ul>)
    }
    const fullJsx = (
      <div>
        <p style={{maxWidth: '400px', textAlign: 'left'}}>{openDeleteDialog?.message}</p>
        <ul style={{}}>
          {jsx.map((item: any) => item)}
        </ul>
      </div>
    )
    return fullJsx
  }, [openDeleteDialog])

  const deleteStudy = (studyId: any) => {
    dispatch(setLoadingState1(ELoadingStateTypes.DELETE_COVERAGE_REQUIREMENTS_STUDY, true, {id: studyId}))
    deleteStudyHandle({studyId})
      .then((res: any) => {
        if (res?.type === 'error') {
          const errorMsg = res?.message ?? "Error deleting study."
          openDeleteDialog({
            message: errorMsg,
            data: {studyId}
          })
          return
        }
        setStudiesData(res)
        setOpenDeleteDialog(null)
      })
      .catch(err => {
        console.log('err', err)
        setOpenDeleteDialog({
          deleteInvalid: true,
          message: 'Failed to delete coverage analysis study.',
          data: {id: studyId}
        })
      }).finally(() => {
      dispatch(setLoadingState1(ELoadingStateTypes.DELETE_COVERAGE_REQUIREMENTS_STUDY, false, {id: studyId}))
      fetchData()
    })
  }
  const initialState = {
    columnFilters: [
      { id: 'isStudyArchive', value: "false" }, // Set initial filter for 'isStudyArchive' column
    ],
  }
  const deleteDialogMessage = openDeleteDialog?.data?.mapAdasProgramToReports ? jsxBulletedList : openDeleteDialog?.message
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container>
        <ArgosTable
          uid={'covreq_1'}
          columns={tableColumns}
          data={studiesData}
          initialState={initialState}

          enableRowActions
          positionActionsColumn="last"
          renderRowActions={({row}: any) => tableRowActions(row, userId, isUserAdmin, userAccessLevel, dispatch, setOpenDeleteDialog, shouldDeleteCoverageRequirementsStudy)}
          renderToolbarInternalActions={({table}: any) => tableToolbarActions(table, isUserAdmin, userAccessLevel, dispatch)}

          state={{ 
            isLoading: isTableLoading,
            columnPinning: {
              left: ['mrt-row-actions', 'name'],
            },
          }}

          displayColumnDefOptions={{
            'mrt-row-actions': {
              size: hasWriteAccess ? 180 : 120,
            },
          }}
          muiExpandButtonProps={({row}) => ({
            // Show expand button only when row has sub-elements
            sx: {
              display: row.subRows?.length ? 'flex' : 'none'
            }
          })}
        />
        <StyledArgosDialog
          width={dialogWidth}
          height={dialogHeight}
          open={isAddStudyDialogOpen}
        >
          <AddStudyDialog addStudy={addStudy}/>
        </StyledArgosDialog>

        <ArgosDialog open={openDeleteDialog != null} onClose={() => setOpenDeleteDialog(null)}
                     width={'auto'} height={'auto'} headerTitle={'Delete Coverage Analysis Study'} headerTitleAlign={'left'}>
          <BoxWithCenteredContent horizontal vertical
                                  style={{height: '100%', display: 'flex', flexFlow: 'column nowrap'}}>
            {openDeleteDialog != null &&
              <Loader isLoading={initialDeleteDialogLoading}>
                {!openDeleteDialog?.data?.mapAdasProgramToReports ?
                  <p style={{width: '300px', textAlign: 'center'}}>
                    {deleteDialogMessage}
                  </p>
                  : <ArgosFlexbox vertical alignItems={'center'} style={{}}>
                    {jsxBulletedList}
                  </ArgosFlexbox>
                }
              </Loader>
            }
            <ArgosFlexbox>
              {!initialDeleteDialogLoading &&
                <ArgosButton
                  // disabled={isDeleteProgramLoading && !initialDeleteDialogLoading} // TODO: If do this then dialog reopens.
                  disabled={isDeleteStudyLoading}
                  onClick={() => setOpenDeleteDialog(null)}>CANCEL</ArgosButton>
              }
              {
                !openDeleteDialog?.deleteInvalid && !initialDeleteDialogLoading && openDeleteDialog != null &&
                openDeleteDialog?.data?.id &&
                <ArgosLoadingButton
                  disabled={isDeleteStudyLoading}
                  isLoading={isDeleteStudyLoading}
                  onClick={() => {
                    deleteStudy(openDeleteDialog?.data?.id)
                  }}
                >
                  DELETE
                </ArgosLoadingButton>
              }
            </ArgosFlexbox>
          </BoxWithCenteredContent>
        </ArgosDialog>
        <StyledArgosDialog
          width={dialogWidth}
          height={dialogHeight}
          open={isEditStudyDialogOpen}
          onClose={() => {
            dispatch(setIsEditStudyDialogOpen(false))
          }}
        >
          <EditStudyDialog editStudy={editStudy}/>
        </StyledArgosDialog>
      </Container>
    </LocalizationProvider>
  )
}

export default Table
